import type { StorefrontVitals } from '@xshop/shared';

// How fast this page view felt to the shopper, measured the way Google's Core Web Vitals are, with the browser's own
// PerformanceObserver: when the main content showed (LCP), how quickly the page answered a tap (INP), how much it
// jumped (CLS), first paint (FCP) and when the server answered (TTFB). Sent once, as the page is left, for the Online
// Store page's speed strip. Anonymous: a few numbers, never who the shopper is. Silent wherever a browser lacks a metric.

const ENDPOINT = '/api/v1/storefront-vitals';
/** Shifts this close together belong to one burst, as Core Web Vitals groups them. */
const SHIFT_GAP_MS = 1000;
/** A burst of shifts is cut off after this long. */
const SHIFT_WINDOW_MS = 5000;
/** Interactions shorter than this are not worth a report. */
const EVENT_THRESHOLD_MS = 40;

/** A layout-shift entry, which TypeScript's DOM types do not name. */
type LayoutShift = PerformanceEntry & { value: number; hadRecentInput: boolean };
/** An event-timing entry with the interaction it belongs to. */
type EventTiming = PerformanceEntry & { interactionId?: number };

// Watches one kind of entry, including those already recorded, and ignores a browser that does not know it.
function observe(type: string, onEntries: (entries: PerformanceEntry[]) => void, durationThreshold?: number): void {
  try {
    new PerformanceObserver((list) => onEntries(list.getEntries())).observe({ type, buffered: true, ...(durationThreshold ? { durationThreshold } : {}) } as PerformanceObserverInit);
  } catch {
    // A browser without this entry type simply reports less.
  }
}

// Adds up layout shift as Core Web Vitals counts it: the worst burst of shifts not caused by the shopper's own input.
function shiftCounter(): { add: (entries: PerformanceEntry[]) => void; worst: () => number | undefined } {
  let worst: number | undefined;
  let burst = 0;
  let first = 0;
  let last = 0;
  // Adds new shifts to the current burst, or starts a new one, keeping the worst burst seen.
  const add = (entries: PerformanceEntry[]) => {
    for (const entry of entries as LayoutShift[]) {
      if (entry.hadRecentInput) continue;
      const joins = burst > 0 && entry.startTime - last < SHIFT_GAP_MS && entry.startTime - first < SHIFT_WINDOW_MS;
      burst = joins ? burst + entry.value : entry.value;
      first = joins ? first : entry.startTime;
      last = entry.startTime;
      worst = Math.max(worst ?? 0, burst);
    }
  };
  return { add, worst: () => worst ?? (PerformanceObserver.supportedEntryTypes?.includes('layout-shift') ? 0 : undefined) };
}

// Keeps each interaction's longest event; the page's INP is its longest interaction, or near the 98th percentile of many.
function interactionCounter(): { add: (entries: PerformanceEntry[]) => void; inp: () => number | undefined } {
  const longest = new Map<number, number>();
  // Keeps the longest event of each interaction.
  const add = (entries: PerformanceEntry[]) => {
    for (const entry of entries as EventTiming[]) {
      if (entry.interactionId) longest.set(entry.interactionId, Math.max(longest.get(entry.interactionId) ?? 0, entry.duration));
    }
  };
  // Reads the page's INP from the interactions kept so far.
  const inp = () => {
    const sorted = [...longest.values()].sort((a, b) => b - a);
    return sorted.length ? sorted[Math.min(sorted.length - 1, Math.floor(sorted.length / 50))] : undefined;
  };
  return { add, inp };
}

// Posts what was measured, once; a lost beacon loses a measurement, never anything the shopper sees.
function send(vitals: StorefrontVitals): void {
  if (Object.values(vitals).every((value) => value === undefined)) return;
  const body = new Blob([JSON.stringify(vitals)], { type: 'text/plain;charset=UTF-8' });
  try {
    if (!navigator.sendBeacon?.(ENDPOINT, body)) void fetch(ENDPOINT, { method: 'POST', body, keepalive: true }).catch(() => undefined);
  } catch {
    // Measuring is never allowed to surface an error into the store.
  }
}

// Starts measuring this page view and sends the result when the shopper hides or leaves the page. A page opened in a
// background tab is not measured: its paint times say when the shopper looked, not how fast the store was.
export function startWebVitals(): void {
  // One measurement per page: the page marks itself, so a second mount of the shell starts none.
  if (typeof PerformanceObserver === 'undefined' || document.visibilityState === 'hidden' || document.documentElement.dataset.vitals) return;
  document.documentElement.dataset.vitals = 'on';
  const vitals: StorefrontVitals = {};
  const nav = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined;
  if (nav && nav.responseStart > 0) vitals.ttfbMs = nav.responseStart;
  observe('paint', (entries) => { const fcp = entries.find((e) => e.name === 'first-contentful-paint'); if (fcp) vitals.fcpMs = fcp.startTime; });
  observe('largest-contentful-paint', (entries) => { const latest = entries[entries.length - 1]; if (latest) vitals.lcpMs = latest.startTime; });
  const shifts = shiftCounter();
  observe('layout-shift', shifts.add);
  const interactions = interactionCounter();
  observe('event', interactions.add, EVENT_THRESHOLD_MS);
  observe('first-input', interactions.add);
  let sent = false;
  // Sends the measurements once, whichever of hiding or leaving comes first.
  const finish = () => {
    if (sent) return;
    sent = true;
    send({ ...vitals, cls: shifts.worst(), inpMs: interactions.inp() });
  };
  addEventListener('visibilitychange', () => { if (document.visibilityState === 'hidden') finish(); }, { capture: true });
  addEventListener('pagehide', finish, { capture: true });
}
